import { models, stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IQueryField } from '@kurtosys/types/query/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration';
import { MockData } from '../../../configuration/mockData';
import { IComponentStyles, IConfiguration, TStoreContext } from '../../../models';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents';
import { ICopyButtonConfiguration } from '../models/ICopyButtonConfiguration';
import { IDocumentList } from '../models/IDocumentList';

import { DocumentListStore } from './DocumentListStore';
import { DownloadStore } from './DownloadStore';
import { EntityStore } from './EntityStore';
type IDisclaimersProps = components.overview.Disclaimers.models.IDisclaimersProps;

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;
type TranslationStore = stores.TranslationStore<IConfiguration, IComponentStyles>;

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	@observable entities = [];

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		makeObservable(this);
	}

	async customInitializeAfter() {
		await this.fetchEntities();
		this.initializeDocumentListStores();
	}

	@computed
	get hasData(): boolean {
		return this.hasDocuments;
	}

	@computed
	get copyButtonProps(): ICopyButtonConfiguration | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.copyButtonProps;
	}

	@computed
	get showCopyButton(): boolean | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.showCopyButton;
	}

	@computed
	get entityGrouping() {
		if (this.appComponentConfiguration) {
			return this.appComponentConfiguration.entityGrouping;
		}
	}

	@computed
	get entitiesId() {
		if (this.entityGrouping) {
			return this.entityGrouping.entitiesId;
		}
	}

	@action
	async fetchEntities() {
		if (this.queryStore && this.entitiesId) {
			const response = await this.queryStore.entitiesRequest(this.entitiesId, {});
			if (response && response.values) {
				this.entities = response.values.map(
					(entity) => new EntityStore(this.storeContext, this.manifest, entity),
				);
			}
		}
	}

	initializeDocumentListStores() {
		if (!this.entitiesId) {
			this.getDocumentListStores();
		}
	}

	@observable documentListStores: DocumentListStore[] = [];

	getDocumentListStores(entityStore?: EntityStore): DocumentListStore[] {
		const stores = this.documentLists.map((documentList: IDocumentList) => {
			const documentFields = (documentList && documentList.documentFields) || {};
			const fields = Object.entries(documentFields).map(([key, value]) => {
				return {
					name: key,
					...(value as object),
				} as IQueryField;
			});
			for (const documentQuery of documentList.documentQueries || []) {
				documentQuery.fields = fields;
			}

			// Apply the app level show copy button flag if not defined on the specific document list
			if (documentList.showCopyButton === undefined && this.showCopyButton !== undefined) {
				documentList.showCopyButton = this.showCopyButton;
			}
			// Apply the app level copy button props if not defined on the specific document list
			if (documentList.copyButtonProps === undefined && this.copyButtonProps !== undefined) {
				documentList.copyButtonProps = this.copyButtonProps;
			}

			return new DocumentListStore(documentList, this.storeContext, this.manifest, entityStore);
		});
		this.documentListStores.push(...stores);
		return stores;
	}

	@computed
	get downloadStore(): DownloadStore {
		return new DownloadStore(this.storeContext, this.manifest);
	}

	@action
	contextsDidUpdateAfter = async () => {
		// TODO: Each Application should put custom logic here to handle changes to the data context
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get showDownloadAll(): boolean {
		if (
			this.appComponentConfiguration &&
			!common.commonUtils.isNullOrUndefined(this.appComponentConfiguration.showDownloadAll)
		) {
			return this.appComponentConfiguration.showDownloadAll;
		}
		return true;
	}

	@computed
	get showDownloadSelectedWhenEmpty(): boolean {
		if (
			this.appComponentConfiguration &&
			!common.commonUtils.isNullOrUndefined(this.appComponentConfiguration.showDownloadSelectedWhenEmpty)
		) {
			return this.appComponentConfiguration.showDownloadSelectedWhenEmpty;
		}
		return true;
	}

	@computed
	get downloadAllLabel(): string {
		const translationStore = this.storeContext.get<TranslationStore>('translationStore');
		const configDownloadAllLabel =
			this.appComponentConfiguration && this.appComponentConfiguration.downloadAllLabel;
		return this.downloadStore.isLoading
			? `${translationStore.translate('Downloading')}...`
			: configDownloadAllLabel || '';
	}

	@computed
	get downloadSelectedLabel(): string {
		const translationStore = this.storeContext.get<TranslationStore>('translationStore');
		const configdownloadSelectedLabel =
			(this.appComponentConfiguration && this.appComponentConfiguration.downloadSelectedLabel) ||
			'Download {selected}';
		const values = {
			selected: this.selectedDocumentIds.length,
		};
		const label = translationStore.translate(configdownloadSelectedLabel, values);
		return this.downloadStore.isLoading ? `${translationStore.translate('Downloading')}...` : label;
	}

	get useSpinnerConfig(): boolean {
		const configUseSpinner = this.appComponentConfiguration && this.appComponentConfiguration.useSpinner;
		return configUseSpinner ? configUseSpinner : false;
	}

	@computed
	get documentIds(): string[] {
		const ids: string[] = [];
		for (const docListStore of this.documentListStores) {
			ids.push(...docListStore.documentIds);
		}
		return ids;
	}

	validateEntitiesDocumentListStores(hasKey: 'hasDocuments' | 'hasSelectedDocuments') {
		for (const entityStore of this.entities) {
			if (this.validateDocumentListStores(entityStore.stores.documentLists, hasKey)) {
				return true;
			}
		}
		return false;
	}

	validateDocumentListStores(
		documentListStores: DocumentListStore[],
		hasKey: 'hasDocuments' | 'hasSelectedDocuments',
	) {
		return documentListStores.reduce((acc: boolean, documentListStore) => {
			return acc || documentListStore[hasKey];
		}, false);
	}

	@computed
	get hasDocuments(): boolean {
		if (this.entities.length > 0) {
			return this.validateEntitiesDocumentListStores('hasDocuments');
		}
		return this.validateDocumentListStores(this.documentListStores, 'hasDocuments');
	}

	@computed
	get hasSelectedDocuments(): boolean {
		if (this.entities.length > 0) {
			return this.validateEntitiesDocumentListStores('hasSelectedDocuments');
		}
		return this.validateDocumentListStores(this.documentListStores, 'hasSelectedDocuments');
	}

	@computed
	get hasAllowSelection(): boolean {
		return this.documentLists.reduce(
			(acc: boolean, documentLists) => acc || documentLists.allowSelection || false,
			false,
		);
	}

	@computed
	get selectedDocumentIds(): string[] {
		const ids: string[] = [];
		this.documentListStores.map((documentListStore) => {
			ids.push(...documentListStore.selectedDocumentIds);
		});
		const valueFn = (i: string) => ids[ids.indexOf(i)];
		return common.commonUtils.unique(ids, valueFn);
	}

	@computed
	get noDocumentsText(): string {
		const noDocumentsTextQueryOptions =
			this.appComponentConfiguration && this.appComponentConfiguration.noDocumentsText;
		return this.getQueryValue(noDocumentsTextQueryOptions);
	}

	@computed
	get hideListsWhenNoDocuments(): boolean {
		return !!(this.appComponentConfiguration && this.appComponentConfiguration.hideListsWhenNoDocuments);
	}

	@computed
	get showDocumentDownloadLists(): boolean {
		if (!this.hasDocuments && this.hideListsWhenNoDocuments) {
			return false;
		}
		return true;
	}

	get disclaimersProps(): IDisclaimersProps | undefined {
		const disclaimersProps = this.appComponentConfiguration && this.appComponentConfiguration.disclaimersProps;
		return this.mergeQueriesAndProps(disclaimersProps);
	}

	@computed
	get documentLists(): IDocumentList[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.documentLists) || [];
	}
}
